import React from 'react';

import {
  HeaderBiddersAPIKey,
  IHeaderBidderListInfo,
} from '~/src/dash/api/headerbidders/headerbidders_models';
import { EBool } from '~/src/dash/api/types/bool';
import {
  generateBidderGroupsOptionsList,
  generateBidderLabelsOptionsList,
  generateBidderTierOptionsList,
} from '~/src/dash/apiV2/bidders';
import { generateChannelsOptionsList } from '~/src/dash/apiV2/channels';
import { generateDemandSourcesOptionsList } from '~/src/dash/apiV2/demandSources';
import { generateOptionsListAsPromise } from '~/src/dash/components/dropdowns/common';
import {
  convertSetIntoArrayOfNumbers,
  convertSetIntoArrayOfStrings,
  convertStatusFilterValueIntoString,
} from '~/src/dash/components/filters/conversions';
import { IExtendedIFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import { StatusOptions } from '~/src/dash/components/filters/status';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import {
  ChipLabelsRender,
  CurrencyRender,
  DateTimeRender,
  EllipsisWithTooltipRender,
  PercentTrendRender,
  PrioritySelectRender,
  SuperUserOnlySwitchRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { Div, Text } from '~/src/dash/publicaUI';

export const formatBiddersResponseIntoLVTRows = (
  bidders: IHeaderBidderListInfo[],
  cfg: IBiddersListViewTable,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  bidders.forEach((bidder) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = bidder[responseKey as keyof IHeaderBidderListInfo];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

interface IFloatNull {
  Float64: number;
  Valid: boolean;
}

const formatFloatNull = (value: IFloatNull) => {
  if (value && value.Valid) {
    return value.Float64;
  }
  return null;
};

const ChannelsListModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Div
      onClick={() => customFunctions.handleClickOnChannelsList(props.row.ID.value)}
      style={{ cursor: 'pointer' }}
    >
      <Text small bold>
        {value}
      </Text>
    </Div>
  );
};

const ChannelGroupsListModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Div
      onClick={() => customFunctions.handleClickOnChannelGroupsList(props.row.ID.value)}
      style={{ cursor: 'pointer' }}
    >
      <Text small bold>
        {value}
      </Text>
    </Div>
  );
};

interface IBiddersListViewTable {
  columns: IListViewTableColumns;
}

export interface IBiddersListViewConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys?: HeaderBiddersAPIKey[]; // default
    isTableExpanded?: boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  columnSelector: {
    resetKeys?: HeaderBiddersAPIKey[]; // what keys should be made active on reset
  };
  filters: Record<string, IExtendedIFilterRowObject>;
  isInitialized?: boolean;
  sorts: HeaderBiddersAPIKey[];
  trends: HeaderBiddersAPIKey[];
  table: IBiddersListViewTable;
  tabKey: string;
}

export const biddersListViewConfig = {
  cypressClassName: 'bidderslistview',
  tabKey: 'All',
  defaultState: {
    activeColumnKeys: [
      'SuperOnly',
      'Bidder',
      'Priority',
      'SiteIDS',
      'ChannelGroupIDS',
      'PastWeekRevenue',
    ],
    isTableExpanded: false,
    order: 'desc',
    orderBy: 'past_week_revenue',
    page: 0,
    rowsPerPage: 25,
  },
  columnSelector: {
    resetKeys: ['SuperOnly', 'Bidder', 'Priority', 'SiteIDS', 'ChannelGroupIDS', 'PastWeekRevenue'],
  },
  filters: {
    Channels: {
      key: 'Channels',
      tKey: 'Channels',
      getList: generateChannelsOptionsList,
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'selectedChannelIDs',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    DemandSources: {
      key: 'DemandSources',
      tKey: 'DemandSources',
      getList: generateDemandSourcesOptionsList,
      selectedLimit: 1,
      extractStringToSort: (option) => option.label,
      queryKey: 'demandSources',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    BidderGroups: {
      key: 'BidderGroups',
      tKey: 'BidderGroups',
      getList: generateBidderGroupsOptionsList,
      selectedLimit: 0,
      enableLabelTooltip: true,
      extractStringToSort: (option) => option.label,
      queryKey: 'bidderGroupIDs',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    BidderLabels: {
      key: 'BidderLabels',
      tKey: 'BidderLabels',
      getList: generateBidderLabelsOptionsList,
      selectedLimit: 0,
      enableLabelTooltip: true,
      extractStringToSort: (option) => option.label,
      queryKey: 'bidderLabels',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    BidderTiers: {
      key: 'BidderTiers',
      tKey: 'BidderTiers',
      getList: generateBidderTierOptionsList,
      selectedLimit: 0,
      disableSort: true,
      queryKey: 'selectedPriorities',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    Status: {
      key: 'Status',
      tKey: 'Status',
      getList: () => generateOptionsListAsPromise(StatusOptions),
      selectedLimit: 1,
      isSelectionRequired: true,
      selectedOptionIDs: new Set([2]), // Active
      disableSearch: true,
      disableSort: true,
      displayLabelWithSelection: true,
      selectedOptionIDsOnReset: new Set([0]), // Show All
      queryKey: 'activeBidderFilter',
      formatQueryValue: convertStatusFilterValueIntoString,
    },
  },
  sorts: ['CreatedAt', 'UpdatedAt', 'PastWeekRevenue', 'PastWeekImpressions'],
  trends: ['PastWeekRevenueTrend', 'PastWeekImpressionsTrend'],
  table: {
    columns: {
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Status: {
        defaultOrderDirection: 'desc',
        formatValue: (value: number) => value === EBool.EBool_TRUE,
        headerRenderType: 'switch',
        key: 'Status',
        tKey: 'Status',
        label: 'Status',
        requestKey: 'active',
        responseKey: 'Active',
        valueType: 'boolean',
      },
      Name: {
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name_link',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Name',
        tKey: 'Name',
        requestKey: 'name',
        responseKey: 'Name',
        valueType: 'string',
      },
      BidderLabelsArray: {
        cellJSX: ChipLabelsRender,
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        isSearchable: true,
        key: 'BidderLabelsArray',
        label: 'Bidder Label',
        tKey: 'BidderLabel',
        requestKey: 'bidder_label_array',
        responseKey: 'BidderLabelsArray',
        valueType: 'array',
      },
      Actions: {
        cellRenderType: 'actions',
        disableSort: true,
        headerRenderType: 'actions',
        key: 'Actions',
        label: 'Actions',
        tKey: 'Actions',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
      Bidder: {
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        isDefaultActive: true,
        isSearchable: true,
        key: 'Bidder',
        label: 'Demand Source',
        tKey: 'DemandSource',
        orderByKey: 'demand_source_ui_to_db_name.ui_name',
        requestKey: 'demand_sources',
        responseKey: 'Bidder',
        valueType: 'string',
      },
      Priority: {
        cellJSX: PrioritySelectRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number_select',
        isDefaultActive: true,
        key: 'Priority',
        label: 'Tier',
        tKey: 'Tier',
        orderByKey: 'priority',
        requestKey: 'priority',
        responseKey: 'Priority',
        valueType: 'number',
      },
      PastWeekRevenue: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Revenue Last 7 Days',
        headerTooltipTKey: 'TooltipRevL7D',
        isDefaultActive: true,
        isNumeric: true,
        key: 'PastWeekRevenue',
        label: 'Rev. (L7D)',
        tKey: 'RevL7D',
        orderByKey: 'past_week_revenue',
        requestKey: '',
        responseKey: 'PastWeekRevenue',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Revenue',
        sortLabelTKey: 'TopRevenue',
        trendKey: 'PastWeekRevenueTrend',
        valueType: 'number',
      },
      PastWeekRevenueTrend: {
        cellJSX: PercentTrendRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        formatValue: formatFloatNull,
        headerRenderType: 'number',
        headerTooltipText: 'Revenue Trend L7D Compared to Previous Period',
        headerTooltipTKey: 'TooltipRevenueTrend',
        isDefaultActive: true,
        key: 'PastWeekRevenueTrend',
        label: 'Rev. Trend',
        tKey: 'RevTrend',
        orderByKey: 'past_week_revenue_trend',
        parentKey: 'PastWeekRevenue',
        requestKey: '',
        responseKey: 'PastWeekRevenueTrend',
        valueType: 'number',
      },
      PastWeekImpressions: {
        cellRenderType: 'abbreviated_number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Impressions Last 7 Days',
        headerTooltipTKey: 'TooltipImpL7D',
        isDefaultActive: true,
        isNumeric: true,
        key: 'PastWeekImpressions',
        label: 'Imp. (L7D)',
        tKey: 'ImpL7D',
        orderByKey: 'past_week_impressions',
        requestKey: '',
        responseKey: 'PastWeekImpressions',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Impressions',
        sortLabelTKey: 'TopImpressions',
        trendKey: 'PastWeekImpressionsTrend',
        valueType: 'number',
      },
      PastWeekImpressionsTrend: {
        cellJSX: PercentTrendRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        formatValue: formatFloatNull,
        headerRenderType: 'number',
        headerTooltipText: 'Impressions Trend L7D Compared to Previous Period',
        headerTooltipTKey: 'TooltipImpressionsTrend',
        isDefaultActive: true,
        key: 'PastWeekImpressionsTrend',
        label: 'Imp. Trend',
        tKey: 'ImpTrend',
        orderByKey: 'past_week_impressions_trend',
        parentKey: 'PastWeekImpressions',
        requestKey: '',
        responseKey: 'PastWeekImpressionsTrend',
        valueType: 'number',
      },
      SiteIDS: {
        cellJSX: ChannelsListModalLink,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        formatValue: (value: number[]) => (value || []).length,
        headerRenderType: 'number_small',
        isDefaultActive: true,
        key: 'SiteIDS',
        label: 'Targeted Channels',
        tKey: 'TargetedChannels',
        orderByKey: 'targeted_channels',
        requestKey: '',
        responseKey: 'SiteIDS',
        valueType: 'array',
      },
      ChannelGroupIDS: {
        cellJSX: ChannelGroupsListModalLink,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        formatValue: (value: number[]) => (value || []).length,
        headerRenderType: 'number',
        isDefaultActive: true,
        key: 'ChannelGroupIDS',
        label: 'Targeted Channel Groups',
        tKey: 'TargetedChannelGroups',
        orderByKey: 'targeted_channel_groups',
        requestKey: '',
        responseKey: 'ChannelGroupIDS',
        valueType: 'array',
      },
      CreatedAt: {
        cellJSX: DateTimeRender,
        cellRenderType: 'date',
        defaultOrderDirection: 'desc',
        headerRenderType: 'date',
        isDate: true,
        key: 'CreatedAt',
        label: 'Created Date',
        tKey: 'CreatedDate',
        orderByKey: 'created_at',
        requestKey: '',
        responseKey: 'CreatedAt',
        sortLabel: 'Last Created',
        sortLabelTKey: 'LastCreated',
        valueType: 'date',
      },
      UpdatedAt: {
        cellJSX: DateTimeRender,
        cellRenderType: 'date',
        defaultOrderDirection: 'desc',
        headerRenderType: 'date',
        isDate: true,
        key: 'UpdatedAt',
        label: 'Last Updated',
        tKey: 'LastUpdated',
        orderByKey: 'updated_At',
        requestKey: '',
        responseKey: 'UpdatedAt',
        sortLabel: 'Last Updated',
        sortLabelTKey: 'LastUpdated',
        valueType: 'date',
      },
      SuperOnly: {
        cellJSX: SuperUserOnlySwitchRender,
        cellRenderType: 'simple_switch',
        defaultOrderDirection: 'desc',
        headerRenderType: 'simple_switch',
        isSuperUserOnly: true,
        key: 'SuperOnly',
        label: 'Super-User Only *',
        tKey: 'SuperUserOnly',
        orderByKey: 'super_only',
        requestKey: 'super_only',
        responseKey: 'SuperOnly',
        valueType: 'boolean',
      },
      FlexibleBiddingEnabled: {
        formatValue: (value: number) => value === 2,
        isHidden: true,
        key: 'FlexibleBiddingEnabled',
        label: 'Flexible Bidding Enabled',
        requestKey: '',
        responseKey: 'FlexibleBiddingEnabled',
        valueType: 'boolean',
      },
      FlexibleBiddingThresholds: {
        isHidden: true,
        key: 'FlexibleBiddingThresholds',
        label: 'Flexible Bidding Thresholds',
        requestKey: '',
        responseKey: 'FlexibleBiddingThresholds',
        valueType: 'array',
      },
    },
  },
} as IBiddersListViewConfig;
